import { createProjectFromMyIp, MyIpRequestError, normalizeIpId } from './myIpService';
import type { MyIpCreationResult } from './types';

interface MyIpProjectActionOptions {
  ipId: string;
  createKey: () => string;
  navigate: (result: MyIpCreationResult) => Promise<unknown>;
  /** 账号或空间切换后，即使旧请求成功也不能导航到旧空间。 */
  isCurrent: () => boolean;
  create?: typeof createProjectFromMyIp;
}

/** 一次用户动作共享请求与幂等键；跳转失败后只重试已有项目的导航。 */
export function createMyIpProjectAction({
  ipId, createKey, navigate, isCurrent, create = createProjectFromMyIp,
}: MyIpProjectActionOptions) {
  const id = normalizeIpId(ipId);
  let key: string | undefined;
  let result: MyIpCreationResult | undefined;
  let pending: Promise<MyIpCreationResult> | undefined;

  /** 校验所选 IP 的服务端初始化结果，再按真实项目和房间导航。 */
  async function execute(): Promise<MyIpCreationResult> {
    if (!isCurrent()) throw new MyIpRequestError('IP action context expired');
    if (!key) key = createKey();
    if (!result) result = await create(id, key);
    if (!isCurrent()) throw new MyIpRequestError('IP action context expired');
    if (result.invalid.length || result.selectedCount !== 1
      || result.addedCount + result.existingCount !== 1) {
      throw new MyIpRequestError('IP initialization is incomplete');
    }
    const navigated = await navigate(result);
    // Next 路由可用 false 表示取消，不能误判成跳转成功。
    if (navigated === false) throw new MyIpRequestError('IP project navigation cancelled');
    return result;
  }

  /** 并发鼠标/键盘激活返回同一 Promise，网络失败后仍使用原幂等键。 */
  function run(): Promise<MyIpCreationResult> {
    if (!pending) {
      pending = execute().finally(() => { pending = undefined; });
    }
    return pending;
  }

  return { run };
}
